/**
 * Sanitizes a variable name to snake_case format
 * @param variableName - The original variable name
 * @returns The sanitized variable name in snake_case
 */
export const sanitizeVariableName = (variableName: string): string => {
  return variableName
    .toLowerCase()
    .replace(/[^a-z0-9\s_]/g, "") // Remove special characters except spaces and underscores
    .replace(/\s+/g, "_") // Replace spaces with underscores
    .replace(/_+/g, "_") // Replace multiple consecutive underscores with single underscore
    .replace(/^_|_$/g, ""); // Remove leading and trailing underscores
};

/**
 * Extracts variable names from text containing placeholders like {{variableName}}
 * @param text - The text to extract variables from
 * @returns Array of variable names found in the text
 */
export const extractVariablesFromText = (text: string): string[] => {
  const regex = /\{\{([^}]+)\}\}/g;
  const matches: string[] = [];
  let match;

  while ((match = regex.exec(text)) !== null) {
    matches.push(match[1]);
  }

  return matches;
};

/**
 * Converts a sanitized variable name back to its original name by comparing with default placeholders
 * @param sanitizedName - The sanitized variable name (e.g., "first_name")
 * @param defaultPlaceholders - Array of default placeholder objects with name property
 * @returns The original variable name if found, otherwise returns the sanitized name formatted
 */
export const unsanitizeVariableName = (sanitizedName: string, defaultPlaceholders: { name: string }[]): string => {
  // Try to find a match in default placeholders
  for (const placeholder of defaultPlaceholders) {
    if (sanitizeVariableName(placeholder.name) === sanitizedName) {
      return placeholder.name;
    }
  }

  // If not found in defaults, return the sanitized name as-is
  // (don't convert to Title Case — it causes duplicates with CSV column names like first_name vs First Name)
  return sanitizedName;
};
